import { Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface LanguageProficiencySelectorProps {
  value?: string;
  isError?: boolean;
  setValue?: (value: string) => void;
  isDisabled?: boolean;
}

export const PROFICIENCY_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

export function LanguageProficiencySelector({
  value,
  setValue,
  isDisabled = false,
  isError = false,
}: LanguageProficiencySelectorProps) {
  const { t } = useTranslation(["common"]);
  const [selectedLevel, setSelectedLevel] = useState(
    value ?? PROFICIENCY_LEVELS[0],
  );

  const handleChange = (level: string) => {
    setSelectedLevel(level);
    setValue?.(level);
  };

  return (
    <div className="w-full">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            disabled={isDisabled}
            className={cn(
              "w-full justify-between h-auto py-2 px-2 transition-all duration-300 overflow-hidden text-ellipsis flex-shrink whitespace-normal",
              isError ? "border-destructive" : "",
            )}
          >
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-start">
                <span className="font-medium text-foreground">
                  {value && value !== ""
                    ? t("common:proficiencyLevels." + selectedLevel)
                    : t("common:proficiencyLevels.selectPlaceholder")}
                </span>
              </div>
            </div>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="p-2 bg-popover border-border">
          {PROFICIENCY_LEVELS.map((level) => (
            <DropdownMenuItem
              key={level}
              onClick={() => handleChange(level)}
              className={`flex items-center gap-3 p-3 cursor-pointer rounded-md transition-all duration-200 ${
                selectedLevel === level ? `bg-accent/50` : "hover:bg-accent/30"
              }`}
            >
              <div className="flex-1 flex flex-col">
                <span className="font-medium text-sm text-foreground">
                  {t("common:proficiencyLevels." + level)}
                </span>
                <span className="hidden md:inline text-xs text-muted-foreground">
                  {t("common:proficiencyDescription." + level)}
                </span>
              </div>
              {selectedLevel === level && (
                <Check className="h-4 w-4 text-foreground" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
